import { useEffect, useRef, useCallback } from 'react';
import type { Map } from 'mapbox-gl';
import { useMap } from '../../hooks/useMap.js';
import { useDrawing } from '../../hooks/useDrawing.js';
import { useMapStore } from '../../stores/mapStore.js';
import { MarkerLayer } from './MarkerLayer.js';
import { LocationMarkerLayer } from './LocationMarkerLayer.js';
import { RouteLayer } from './RouteLayer.js';
import { AnnotationLayer } from './AnnotationLayer.js';

// Layer ID substrings to hide in map mode (everything except roads + road labels)
const MAP_MODE_HIDE_PATTERNS = [
  'poi', 'building', 'transit', 'airport', 'natural', 'waterway',
  'landuse', 'national-park', 'aeroway', 'settlement-label',
  'state-label', 'country-label',
];

const PUBLIC_MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN as string;

interface MapCanvasProps {
  onMapReady?: (map: Map) => void;
}

export function MapCanvas({ onMapReady }: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { mapRef, initMap } = useMap();
  const { initDraw } = useDrawing();
  const {
    showTraffic, mapMode,
    activeAnnotationTool, addAnnotation, setActiveAnnotationTool,
  } = useMapStore();
  const hiddenLayersRef = useRef<string[]>([]);

  // ─── Map initialisation (once) ────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const map = initMap(containerRef.current, PUBLIC_MAPBOX_TOKEN);
    if (!map) return;
    map.on('load', () => {
      initDraw(map);
      onMapReady?.(map);
    });
    return () => { map.remove(); mapRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Traffic layer ────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    if (showTraffic) {
      if (!map.getSource('mapbox-traffic')) {
        map.addSource('mapbox-traffic', { type: 'vector', url: 'mapbox://mapbox.mapbox-traffic-v1' });
      }
      if (!map.getLayer('traffic')) {
        map.addLayer({
          id: 'traffic',
          type: 'line',
          source: 'mapbox-traffic',
          'source-layer': 'traffic',
          paint: {
            'line-width': 2,
            'line-color': ['match', ['get', 'congestion'],
              'low', '#4caf50', 'moderate', '#ff9800', 'heavy', '#f44336', 'severe', '#9c27b0', '#ccc'],
          },
        });
      }
    } else {
      if (map.getLayer('traffic')) map.removeLayer('traffic');
    }
  }, [showTraffic, mapRef]);

  // ─── Map mode (disable drag + hide POI / label layers) ────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    if (mapMode) {
      map.dragPan.disable();
      map.dragRotate.disable();
      map.scrollZoom.disable();
      const toHide = map.getStyle().layers
        .filter(l => MAP_MODE_HIDE_PATTERNS.some(p => l.id.includes(p)))
        .map(l => l.id);
      hiddenLayersRef.current = toHide;
      toHide.forEach(id => {
        try { map.setLayoutProperty(id, 'visibility', 'none'); } catch { /* ok */ }
      });
    } else {
      map.dragPan.enable();
      map.dragRotate.enable();
      map.scrollZoom.enable();
      hiddenLayersRef.current.forEach(id => {
        try { map.setLayoutProperty(id, 'visibility', 'visible'); } catch { /* ok */ }
      });
      hiddenLayersRef.current = [];
    }
  }, [mapMode, mapRef]);

  // ─── Crosshair cursor when annotation tool is active ─────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = activeAnnotationTool ? 'crosshair' : '';
  }, [activeAnnotationTool, mapRef]);

  // ─── Escape clears active annotation tool ────────────────────────────────
  useEffect(() => {
    if (!activeAnnotationTool) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveAnnotationTool(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [activeAnnotationTool, setActiveAnnotationTool]);

  // ─── Annotation click handler on the container div ────────────────────────
  // Using a React onClick (rather than map.on('click')) avoids mapboxgl-draw
  // event interception and always reads the current annotation tool from state.
  const handleContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!activeAnnotationTool) return;
    const target = e.target as HTMLElement;
    // Ignore clicks on existing annotations (handled by dblclick on the marker)
    if (target.closest('.annotation-marker')) return;
    const map = mapRef.current;
    if (!map) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const lngLat = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);

    if (activeAnnotationTool === 'text') {
      const label = window.prompt('Text label:');
      if (!label?.trim()) return;
      addAnnotation({ id: crypto.randomUUID(), type: 'text', lat: lngLat.lat, lng: lngLat.lng, label: label.trim() });
    } else {
      addAnnotation({ id: crypto.randomUUID(), type: activeAnnotationTool, lat: lngLat.lat, lng: lngLat.lng });
    }
  }, [activeAnnotationTool, addAnnotation, mapRef]);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="absolute inset-0" onClick={handleContainerClick} />
      {mapRef.current && (
        <>
          <MarkerLayer map={mapRef.current} />
          <LocationMarkerLayer map={mapRef.current} />
          <RouteLayer />
          <AnnotationLayer map={mapRef.current} />
        </>
      )}
    </div>
  );
}
